// Profile info tab — user avatar card, usage stats, multi-org list.

import { Card } from "@/components/ui/Card";
import { Stat } from "@/components/ui/Stat";
import { useDashboardStats } from "@/hooks/useDashboard";
import { useAuthStore } from "@/stores/authStore";
import { DeleteAccountCard } from "@/components/profile/DeleteAccountCard";
import { useDict } from "@/i18n/LocaleProvider";

export function ProfileInfoTab() {
  const t = useDict().app.profile.info;
  const user = useAuthStore((s) => s.user);
  const { data: stats } = useDashboardStats();
  const primary = user?.organizations?.[0];

  if (!user) return null;
  const initial = (user.full_name || user.email || "?").charAt(0).toUpperCase();

  return (
    <div className="flex flex-col gap-6">
      <Card>
        {/* Top row: avatar + identity (left), org chip (right). On
            mobile the org chip drops below the identity block so the
            avatar + name don't get squashed. min-w-0 + truncate prevent
            long emails / names from leaking out of the card. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center font-display text-[28px] font-bold text-accent shrink-0"
              style={{
                background: "rgb(var(--color-accent-bg))",
                border: "2px solid rgb(var(--color-accent))",
              }}
            >
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-[20px] font-bold text-content truncate">
                {user.full_name || t.anonymous}
              </div>
              <div className="font-mono text-[12px] text-muted mt-[2px] truncate">
                {user.email}
              </div>
              {/* Auth + email-verified on their own lines so a long auth
                  provider name doesn't push the verified state to a
                  second line awkwardly. */}
              <div className="font-mono text-[10px] text-dim mt-[6px]">
                {t.auth} {user.auth_provider || "local"}
              </div>
              <div
                className="font-mono text-[10px] mt-[2px]"
                style={{ color: user.email_verified ? "rgb(var(--color-success))" : "rgb(var(--color-warning))" }}
              >
                {user.email_verified ? t.emailVerified : t.emailNotVerified}
              </div>
            </div>
          </div>
          {primary && (
            <div className="text-left sm:text-right shrink-0">
              <div className="font-mono text-[9px] text-muted uppercase tracking-[0.05em]">
                {t.currentOrg}
              </div>
              <div className="font-display text-[16px] font-bold text-content mt-[3px] break-words">
                {primary.name}
              </div>
              <div
                className="inline-block mt-1 px-2 py-[3px] rounded-[3px] font-mono text-[9px] font-bold uppercase tracking-[0.05em]"
                style={{
                  background: "rgb(var(--color-accent-bg))",
                  border: "1px solid rgb(var(--color-accent) / 0.4)",
                  color: "rgb(var(--color-accent))",
                }}
              >
                {primary.role}
              </div>
            </div>
          )}
        </div>
      </Card>

      {stats && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card>
              <Stat
                label={t.totalScans}
                value={stats.engagements.total}
                color="rgb(var(--color-accent))"
              />
            </Card>
            <Card>
              <Stat
                label={t.findings}
                value={stats.findings.total}
                color="rgb(var(--color-content))"
              />
            </Card>
            <Card>
              <Stat
                label={t.toolsExecuted}
                value={stats.executions.total}
                color="rgb(var(--color-info))"
              />
            </Card>
            <Card>
              <Stat
                label={t.evidence}
                value={stats.evidence.total}
                color="rgb(var(--color-brand))"
              />
            </Card>
          </div>

          {user.organizations.length > 1 && (
            <Card>
              <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
                {t.allOrgs}
              </div>
              {user.organizations.map((o) => (
                <div
                  key={o.id}
                  className="flex justify-between items-center py-2 border-b border-stroke"
                >
                  <div>
                    <div className="font-sans text-[13px] text-content font-semibold">
                      {o.name}
                    </div>
                    <div className="font-mono text-[10px] text-muted">
                      {o.slug}
                    </div>
                  </div>
                  <span
                    className="px-2 py-[3px] rounded-[3px] font-mono text-[9px] font-bold uppercase"
                    style={{
                      background: "rgb(var(--color-accent-bg))",
                      border: "1px solid rgb(var(--color-accent) / 0.4)",
                      color: "rgb(var(--color-accent))",
                    }}
                  >
                    {o.role}
                  </span>
                </div>
              ))}
            </Card>
          )}
        </>
      )}

      <DeleteAccountCard />
    </div>
  );
}
